import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApolloError, useMutation } from '@apollo/client';
import { useBreakpoint } from '@moda/om';
import { DesignerQuery, Gender } from '../../generated/types';
import { reportError } from '../../lib/reportError';
import { useEmailVerification } from '../useEmailVerification';
import {
  SignUpSource,
  DesignerEmailSubscribeMutationVariables,
  EmailSubscribeMutation,
  EMAIL_SUBSCRIBE_MUTATION
} from './useSubscribe.rest';

type Designer = NonNullable<DesignerQuery['designer']>;

export const useDesignerSubscribe = () => {
  const { vertical = 'women' } = useParams<{
    vertical: Gender | undefined;
    trunkshowSlug: string | undefined;
    designerSlug: string | undefined;
  }>();
  const [isSubscribed, setIsSubscribed] = useState(false);

  const [mutate, { data, loading, error }] = useMutation<
    EmailSubscribeMutation,
    DesignerEmailSubscribeMutationVariables
  >(EMAIL_SUBSCRIBE_MUTATION);

  const handleSuccess = useCallback(() => setIsSubscribed(true), []);

  const handleError = useCallback(
    (caughtError: ApolloError) => reportError('DESIGNER_SUBSCRIBE', caughtError),
    []
  );

  const isMobile = useBreakpoint({ lt: 'md' }).matches;
  const { isValid: isEmailValid, error: mailValidationError } = useEmailVerification();

  const subscribeToDesigner = useCallback(
    (email: string, designer: Designer) => {
      const source: SignUpSource = isMobile ? 'mobile' : 'desktop';

      if (!isEmailValid(email)) return;

      mutate({
        variables: {
          input: {
            user: {
              email,
              gender: Gender[vertical],
              phone: ''
            },
            label: 'trunkshow',
            category: 'Marketing Signup',
            sign_up_source_target: source,
            entity_type: 'Trunkshow',
            entity_id: Number(designer.id),
            trunkshow_preview: true
          }
        }
      })
        .then(handleSuccess)
        .catch(handleError);
    },
    [handleError, handleSuccess, isEmailValid, isMobile, mutate, vertical]
  );

  return {
    subscribeToDesigner,
    data,
    mailValidationError,
    error,
    loading,
    isSubscribed
  };
};
